import { child, onChildAdded, set } from 'firebase/database';
import AdapterMobileV1 from '~/class/AdapterMobileV1';
import { useMobileV1Toast } from '~/composables/useMobileV1Toast';
import { mobileItemIndex } from '~/utils/mobileOrderItemIndex';

// mobile-v2 INGEST — cashier-side answerer for the Android client, using the SAME
// handshake shape as the terminal silo (pos.terminal.ts):
//
//   1. mobile_v2_handshake/{id} : phone writes 1, we flip it to 2 (auto-clear 2s).
//      That flip is the phone's "cashier is online, send it".
//   2. mobile_v2_job/{id}       : phone writes the order JSON. We guard, convert
//      via AdapterMobileV1 — the SAME adapter mobile-v1 and the job gateway use —
//      add it to the jobchain, then delete the node.
//   3. mobile_v2_result/{id}    : we publish { read: 2, jobId, nickName } as the
//      ack, cleared after 10s. The phone reads its own id here to confirm.
//
// What actually changed versus mobile-v1 is ONLY the transport. mobile-v1 has the
// phone write into `tables/{i}/history` and the cashier open one listener per table
// index with a 1-hour lookback window and replay-vs-live disambiguation. Here it is
// one handshake node and one job node, exactly like the terminal. The job building,
// the guards and the jobchain insert are unchanged.
//
// {id} is the ORDER's uniqueid, not a push key: a retry lands on the same node, so
// a duplicate is recognisable instead of becoming a second order and a second
// kitchen slip.
//
// Cashier-only — init is called from bootstrap's initCashierControllerRuntime.
export const usePosMobileV2Store = defineStore("mobile-v2", () => {

  // Stored acks, so a phone retry after we already ingested still gets its jobId
  // back instead of timing out. Same idea as pos.job-gateway's receivedReply.
  const receivedReply = new Map<string, any>()
  // In-flight, i.e. ingested but the ack write has not landed yet.
  const processing = new Set<string>()

  const RESULT_TTL_MS = 10000
  const NODE_CLEAR_MS = 2000

  const normaliseAlias = (rawAlias: any) =>
    String(rawAlias || '').replace('โต๊ะ ', 'T').replace('Table ', 'T')

  // The jobchain key this order WOULD have. AdapterMobileV1 builds
  // jobId = `${uniqueid}__${alias}` and pos.jobs.add() keys the chain by
  // jobId.replaceAll('.', '_'), so this reproduces that string exactly and turns
  // "have I already taken this order?" into one lookup.
  const jobchainKeyFor = (uniqueid: string, rawAlias: any) =>
    `${uniqueid}__${normaliseAlias(rawAlias)}`.replaceAll('.', '_')

  // Durable duplicate check. receivedReply/processing only know about THIS page
  // session, and init() wipes all three RTDB nodes — so without this, a cashier
  // reload between the phone's send and its retry re-ingests the order and prints
  // a SECOND kitchen slip. (pos.jobs.add keys on jobId so the jobchain itself
  // collapses to one node; it is the print hook that fires twice.) The jobchain is
  // RTDB-backed and restored at boot, so it remembers across reloads. This is the
  // same defence pos.mobile-v1.isAlreadyHandled provides, and its comment records
  // that this is literally how one order became four kitchen slips.
  const isAlreadyHandled = (uniqueid: string, rawAlias: any) =>
    receivedReply.has(uniqueid) ||
    !!(usePosJobsStore().jobs as any)[jobchainKeyFor(uniqueid, rawAlias)]

  // Rebuild the ack for an order we ingested in a PREVIOUS session, where
  // receivedReply is empty but the jobchain still has the job.
  const replyFromJobchain = (uniqueid: string, rawAlias: any) => {
    const job = (usePosJobsStore().jobs as any)[jobchainKeyFor(uniqueid, rawAlias)]
    return {
      read: 2,
      jobId: job?.jobId || null,
      nickName: job?.nickName || null,
      alias: job?.alias || normaliseAlias(rawAlias),
    }
  }

  // The dedupe above is only as good as the jobchain behind it: subscribe before
  // it has loaded and every replayed node looks brand new. bootstrap awaits this
  // store's init, and Layer B (jobs) is loaded before initByRunMode runs, so this
  // resolves immediately — it exists so a future re-order of bootstrap stalls
  // loudly instead of silently reprinting the kitchen. Cannot hang on a new shop:
  // jobs.bootOnline uses onValue (fires for a missing node) and races a 3s timeout.
  const waitForJobchainLoaded = () => {
    const jobsStore = usePosJobsStore()
    if (jobsStore.loaded) return Promise.resolve()
    console.warn('[mobile-v2] jobchain not loaded yet — holding mobile ingest until it is')
    return new Promise<void>((resolve) => {
      const stop = watch(() => jobsStore.loaded, (isLoaded) => {
        if (!isLoaded) return
        stop()
        resolve()
      })
    })
  }

  const publishResult = (id: string, payload: any) => {
    const resultRef = child(useRealtimeDatabase().mobileV2ResultRef(), id)
    set(resultRef, payload)
    setTimeout(() => { set(resultRef, null) }, RESULT_TTL_MS)
  }

  const rejectJob = (snapshot: any, id: string, reason: string, toast: string) => {
    // The phone distinguishes accept from reject by `read`: 2 = accepted, 3 =
    // rejected, with `reason` carrying the human message it shows in the alert.
    publishResult(id, { read: 3, reason })
    useMobileV1Toast().push({ variant: 'warn', text: toast, duration: 3500 })
    setTimeout(() => { set(snapshot.ref, null) }, NODE_CLEAR_MS)
  }

  // ---------------------------------------------------------------- handshake

  function watchHandshakeFromMobileV2() {
    const handshakeRef = useRealtimeDatabase().mobileV2HandshakeRef()
    onChildAdded(handshakeRef, (snapshot: any) => {
      const data = snapshot.val()
      // Accept 0 as well as 1: the legacy Java job client writes 0 (see
      // pos.job-gateway.handleHandshake), so tolerating both keeps one Android
      // code path able to talk to either silo.
      if (data == 0 || data == 1) {
        set(snapshot.ref, 2)
        setTimeout(() => { set(snapshot.ref, null) }, NODE_CLEAR_MS)
      }
    })
  }

  // ---------------------------------------------------------------- job intake

  function watchOrderFromMobileV2() {
    const jobRef = useRealtimeDatabase().mobileV2JobRef()
    onChildAdded(jobRef, (snapshot: any) => {
      const raw = snapshot.val()
      if (raw == null) return

      // The phone writes the order as a JSON string, same as mobile-v1 and the job
      // gateway. Tolerate a plain object too, in case a future client stops
      // stringifying.
      let order: any
      let jsonString: string
      try {
        if (typeof raw === 'string') {
          jsonString = raw
          order = JSON.parse(raw)
        } else {
          order = raw
          jsonString = JSON.stringify(raw)
        }
      } catch (e) {
        setTimeout(() => { set(snapshot.ref, null) }, NODE_CLEAR_MS)
        return
      }

      if (!order || order.void || order.read == 2) return

      const id = String(order.uniqueid || snapshot.key || '')
      if (!id) {
        setTimeout(() => { set(snapshot.ref, null) }, NODE_CLEAR_MS)
        return
      }

      // Already ingested → re-publish the ack so a retry succeeds instead of
      // becoming a second order. Falls back to rebuilding the ack from the
      // jobchain when this is a different page session from the one that took it.
      if (isAlreadyHandled(id, order.alias)) {
        const reply = receivedReply.get(id) || replyFromJobchain(id, order.alias)
        receivedReply.set(id, reply)
        publishResult(id, reply)
        setTimeout(() => { set(snapshot.ref, null) }, NODE_CLEAR_MS)
        return
      }
      if (processing.has(id)) return

      // --- guards, same set and same order as pos.mobile-v1 ------------------

      if (usePosJobsStore().isRunningOverClosedDay()) {
        rejectJob(snapshot, id, 'closed_day',
          'ร้านเปิดข้ามวัน — กรุณาปิดร้านแล้วเปิดใหม่ก่อนรับออเดอร์จากมือถือ')
        return
      }

      const pairedDevices = usePosMobileV1Store().pariedDevices
      if (!pairedDevices[order.deviceId]) {
        rejectJob(snapshot, id, 'unpaired',
          `ออเดอร์จากอุปกรณ์ที่ยังไม่ได้จับคู่ — ${order.deviceId}`)
        return
      }

      const lockAlias = String(order.alias || '').replace('โต๊ะ ', 'T').replace('Table ', 'T')
      if (usePosTableStatesStore().isLockedWithTable(lockAlias)) {
        rejectJob(snapshot, id, 'table_locked', `โต๊ะ ${lockAlias} ถูกล็อกอยู่`)
        return
      }

      // Item-level dedupe, same index as mobile-v1. The check above keys on the
      // order id; this one keys each line on `${alias}_${item.uniqueid}` across
      // the whole day's jobchain, so a line already taken — even on a bill that
      // has since been paid — is never added twice (5224 / T22).
      const { fresh, matchedJobKey } = mobileItemIndex.partition(
        usePosJobsStore().jobs as any, order.alias, order.orderitems)
      if (fresh.length === 0 && matchedJobKey) {
        const matched = (usePosJobsStore().jobs as any)[matchedJobKey]
        const reply = {
          read: 2,
          jobId: matched?.jobId || null,
          nickName: matched?.nickName || null,
          alias: matched?.alias || normaliseAlias(order.alias),
        }
        receivedReply.set(id, reply)
        publishResult(id, reply)
        useMobileV1Toast().push({ variant: 'warn', text: `ออเดอร์ซ้ำ — ${order.alias || ''}`, duration: 2500 })
        setTimeout(() => { set(snapshot.ref, null) }, NODE_CLEAR_MS)
        return
      }
      if (matchedJobKey) {
        order.orderitems = fresh
        jsonString = JSON.stringify(order)
      }

      processing.add(id)

      // --- build the job exactly the way mobile-v1 does ----------------------

      // Same adapter, same shared mappers. applyDefaultDiscount and the combo
      // expandItems both happen INSIDE fromCloudToJobchain, so nothing extra is
      // needed here — which is the point: only the transport changed.
      const job: any = new AdapterMobileV1().fromCloudToJobchain(jsonString)

      // The phone cannot know whether the table is already open; it has no view of
      // the jobchain. The adapter's own isOpeningTable is computed from
      // orderData.table (the numeric id) against a table map keyed by ALIAS, so it
      // does not resolve — mobile-v1 recomputes it for that reason and so do we.
      //   new table → keep the phone's guest count, flag the open
      //   re-add    → force guests 0, so kitchen print falls back to the table's
      //               real count instead of the phone's stale figure
      try {
        const existingItems = usePosJobsStore().tables?.[job.alias]?.items?.length || 0
        const isNewTable = existingItems === 0
        job.isOpeningTable = isNewTable
        if (!isNewTable) job.guests = 0
      } catch (e) {
        console.warn('[mobile-v2] guest/opening reconcile failed, leaving as-is', e)
      }

      // Trust the cashier's server-synced clock, not the phone's, so the jobchain
      // replays in true arrival order (computeJobchain sorts by pcTimestamp||timestamp).
      job.pcTimestamp = useServerTime().nowISO()

      usePosJobsStore().add(job, true)

      // --- ack ---------------------------------------------------------------

      const reply = {
        read: 2,
        jobId: job.jobId || null,
        nickName: job.nickName || null,
        alias: job.alias || null,
      }
      receivedReply.set(id, reply)
      processing.delete(id)
      publishResult(id, reply)

      useMobileV1Toast().push({
        variant: 'success',
        text: `New order from — ${job.alias}`,
        duration: 2000,
      })

      setTimeout(() => { set(snapshot.ref, null) }, NODE_CLEAR_MS)
    })
  }

  // ---------------------------------------------------------------- lifecycle

  function cleanAllRef() {
    set(useRealtimeDatabase().mobileV2HandshakeRef(), null)
    set(useRealtimeDatabase().mobileV2JobRef(), null)
    set(useRealtimeDatabase().mobileV2ResultRef(), null)
  }

  async function init() {
    // Must hold until the jobchain is loaded — isAlreadyHandled reads it, and
    // subscribing early makes every replayed node look new.
    await waitForJobchainLoaded()
    cleanAllRef()
    watchHandshakeFromMobileV2()
    watchOrderFromMobileV2()
  }

  return {
    init,
  }
})
